'use client';

import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

type TypingAnimationProps = {
  messages: string[];
  duration?: number;
  className?: string;
};

function TypingAnimation({
  messages,
  duration = 200,
  className,
}: TypingAnimationProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    const currentMessage = messages[messageIndex] ?? '';

    const timeout = setTimeout(
      () => {
        if (isDeleting) {
          // Deleting phase
          if (charIndex > 0) {
            setDisplayedText(currentMessage.substring(0, charIndex - 1));
            setCharIndex((prev) => prev - 1);
          } else {
            // Move to the next message after deleting
            setIsDeleting(false);
            setMessageIndex((prev) => (prev + 1) % messages.length);
          }
        } else {
          // Typing phase
          if (charIndex < currentMessage.length) {
            setDisplayedText(currentMessage.substring(0, charIndex + 1));
            setCharIndex((prev) => prev + 1);
          } else {
            // Pause at the end of the message before deleting
            setTimeout(() => setIsDeleting(true), 1000);
          }
        }
      },
      isDeleting ? duration / 2 : duration
    );

    return () => clearTimeout(timeout);
  }, [messages, messageIndex, charIndex, isDeleting, duration]);

  return <span className={cn(className)}>{displayedText}</span>;
}

type TypingDisplayProps = {
  messages: string[];
  duration?: number;
  className?: string;
};

export function TypingDisplay({
  messages,
  duration = 200,
  className,
}: TypingDisplayProps) {
  if (messages.length === 0) {
    return null;
  }

  return (
    <span className="relative inline-block bg-black">
      <TypingAnimation
        messages={messages}
        duration={duration}
        className={className}
      />
    </span>
  );
}
